import Cookies from "js-cookie";

import {
  GOOGLE_PLACES_SCRIPT_ID,
  GOOGLE_PLACES_SCRIPT_TIMEOUT_MS,
} from "eventLocation/constants";
import type {
  EventLocationDetails,
  GoogleAddressComponent,
  GoogleMapsApi,
  GoogleModernAddressComponent,
  GoogleModernPlace,
  GoogleModernPlacesApi,
  GooglePlaceDetails,
} from "./types";

const GOOGLE_MAPS_CALLBACK_NAME = "__waylightGoogleMapsLoaded";
const NEARBY_PLACE_SEARCH_RADIUS_METERS = 50;

const GOOGLE_LANGUAGE_BY_WAYLIGHT_LANGUAGE: Record<string, string> = {
  CS: "cs",
  DE: "de",
  EN: "en",
  ES: "es",
  FR: "fr",
  HI: "hi",
  IT: "it",
  PL: "pl",
  PT: "pt",
  RU: "ru",
  UA: "uk",
  UK: "uk",
};

const GOOGLE_REGION_BY_WAYLIGHT_LANGUAGE: Record<string, string> = {
  CS: "CZ",
  DE: "DE",
  ES: "ES",
  FR: "FR",
  HI: "IN",
  IT: "IT",
  PL: "PL",
  PT: "PT",
  RU: "RU",
  UA: "UA",
  UK: "UA",
};

export function getGoogleMapsLocale(languageCode?: string | null) {
  const normalizedLanguage =
    languageCode?.toUpperCase() ||
    Cookies.get("language")?.toUpperCase() ||
    "EN";

  return {
    language: GOOGLE_LANGUAGE_BY_WAYLIGHT_LANGUAGE[normalizedLanguage] || "en",
    region: GOOGLE_REGION_BY_WAYLIGHT_LANGUAGE[normalizedLanguage],
  };
}

export function buildGoogleMapsScriptUrl(
  apiKey: string,
  languageCode?: string | null
) {
  const { language, region } = getGoogleMapsLocale(languageCode);
  const localeParams = region
    ? `&language=${encodeURIComponent(language)}&region=${encodeURIComponent(
        region
      )}`
    : `&language=${encodeURIComponent(language)}`;

  return `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(
    apiKey
  )}&libraries=places&callback=${GOOGLE_MAPS_CALLBACK_NAME}&loading=async${localeParams}`;
}

let googlePlacesScriptPromise: Promise<boolean> | null = null;
let previousGoogleMapsAuthFailure: (() => void) | undefined;
const googleMapsAuthFailureCallbacks = new Set<() => void>();

function addGoogleMapsAuthFailureCallback(callback: () => void) {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  if (!googleMapsAuthFailureCallbacks.size) {
    previousGoogleMapsAuthFailure = (window as any).gm_authFailure;
    (window as any).gm_authFailure = () => {
      previousGoogleMapsAuthFailure?.();
      Array.from(googleMapsAuthFailureCallbacks).forEach((handler) =>
        handler()
      );
    };
  }

  googleMapsAuthFailureCallbacks.add(callback);

  return () => {
    googleMapsAuthFailureCallbacks.delete(callback);
    if (!googleMapsAuthFailureCallbacks.size) {
      (window as any).gm_authFailure = previousGoogleMapsAuthFailure;
      previousGoogleMapsAuthFailure = undefined;
    }
  };
}

function getGooglePlacesApiKey() {
  return process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "";
}

export function getGoogleMapsApi(): GoogleMapsApi | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return (window as any).google?.maps;
}

export function getGooglePlacesApi(): GoogleModernPlacesApi | undefined {
  return getGoogleMapsApi()?.places;
}

function isGoogleMapsLoaded() {
  return Boolean(getGoogleMapsApi()?.Map);
}

async function ensureGoogleMapsLibraries() {
  const mapsApi = getGoogleMapsApi();

  if (!mapsApi) {
    return false;
  }

  if (!mapsApi.Map) {
    try {
      const mapsLibrary = await mapsApi.importLibrary?.("maps");
      if (mapsLibrary?.Map) {
        mapsApi.Map = mapsLibrary.Map;
      }
    } catch (_) {
      return false;
    }
  }

  if (!mapsApi.Marker) {
    try {
      const markerLibrary = await mapsApi.importLibrary?.("marker");
      if (markerLibrary?.Marker) {
        mapsApi.Marker = markerLibrary.Marker;
      }
    } catch (_) {
      // A marker is only needed once a location is selected; the map itself can
      // still render without it.
    }
  }

  return isGoogleMapsLoaded();
}

async function ensureGoogleGeocodingLibrary() {
  const mapsApi = getGoogleMapsApi();

  if (!mapsApi) {
    return false;
  }

  if (!mapsApi.Geocoder || !mapsApi.GeocoderStatus) {
    try {
      const geocodingLibrary = await mapsApi.importLibrary?.("geocoding");
      if (geocodingLibrary?.Geocoder) {
        mapsApi.Geocoder = geocodingLibrary.Geocoder;
      }
      if (geocodingLibrary?.GeocoderStatus) {
        mapsApi.GeocoderStatus = geocodingLibrary.GeocoderStatus;
      }
    } catch (_) {
      return false;
    }
  }

  return Boolean(mapsApi.Geocoder && mapsApi.GeocoderStatus?.OK);
}

function stringifyGoogleText(value?: string | { toString(): string }) {
  return typeof value === "string" ? value : value?.toString() || "";
}

function getAddressComponent(
  components: GoogleAddressComponent[] | undefined,
  types: string[]
) {
  return components?.find((component) =>
    types.some((type) => component.types.includes(type))
  )?.long_name;
}

function getModernAddressComponent(
  components: GoogleModernAddressComponent[] | undefined,
  types: string[]
) {
  return components?.find((component) =>
    types.some((type) => component.types.includes(type))
  )?.longText;
}

function getCityFromPlace(place: GooglePlaceDetails) {
  return (
    getAddressComponent(place.address_components, ["locality"]) ||
    getAddressComponent(place.address_components, ["postal_town"]) ||
    getAddressComponent(place.address_components, [
      "administrative_area_level_2",
    ]) ||
    getAddressComponent(place.address_components, [
      "administrative_area_level_1",
    ])
  );
}

function getCityFromModernPlace(place: GoogleModernPlace) {
  return (
    getModernAddressComponent(place.addressComponents, ["locality"]) ||
    getModernAddressComponent(place.addressComponents, ["postal_town"]) ||
    getModernAddressComponent(place.addressComponents, [
      "administrative_area_level_2",
    ]) ||
    getModernAddressComponent(place.addressComponents, [
      "administrative_area_level_1",
    ])
  );
}

export function parseLocationCoordinates(value: string) {
  const coordinateMatch = value
    .trim()
    .match(/^(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)$/);

  if (!coordinateMatch) {
    return undefined;
  }

  const latitude = Number(coordinateMatch[1]);
  const longitude = Number(coordinateMatch[2]);

  if (
    Number.isNaN(latitude) ||
    Number.isNaN(longitude) ||
    latitude < -90 ||
    latitude > 90 ||
    longitude < -180 ||
    longitude > 180
  ) {
    return undefined;
  }

  return { latitude, longitude };
}

function buildGoogleMapsUrl(
  placeId?: string,
  latitude?: number,
  longitude?: number
) {
  if (latitude !== undefined && longitude !== undefined) {
    const coordinates = `${latitude},${longitude}`;
    const placeQuery = placeId
      ? `&query_place_id=${encodeURIComponent(placeId)}`
      : "";

    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
      coordinates
    )}${placeQuery}`;
  }

  if (!placeId) {
    return undefined;
  }

  return `https://www.google.com/maps/search/?api=1&query_place_id=${encodeURIComponent(
    placeId
  )}`;
}

export function loadGooglePlacesScript(
  languageCode?: string | null
): Promise<boolean> {
  if (typeof window === "undefined") {
    return Promise.resolve(false);
  }

  if (isGoogleMapsLoaded()) {
    return ensureGoogleMapsLibraries();
  }

  if (getGoogleMapsApi()?.importLibrary) {
    return ensureGoogleMapsLibraries();
  }

  if (googlePlacesScriptPromise) {
    return googlePlacesScriptPromise;
  }

  const apiKey = getGooglePlacesApiKey();
  if (!apiKey) {
    return Promise.resolve(false);
  }

  googlePlacesScriptPromise = new Promise((resolve) => {
    let didResolve = false;
    const removeAuthFailureCallback = addGoogleMapsAuthFailureCallback(() => {
      finish(
        false,
        document.getElementById(
          GOOGLE_PLACES_SCRIPT_ID
        ) as HTMLScriptElement | null
      );
    });
    const finish = (isLoaded: boolean, script?: HTMLScriptElement | null) => {
      if (didResolve) {
        return;
      }

      didResolve = true;
      removeAuthFailureCallback();
      delete (window as any)[GOOGLE_MAPS_CALLBACK_NAME];
      if (!isLoaded) {
        googlePlacesScriptPromise = null;
        script?.remove();
      }
      resolve(isLoaded);
    };

    const finishAfterLibrariesLoad = async (
      script?: HTMLScriptElement | null
    ) => {
      finish(await ensureGoogleMapsLibraries(), script);
    };

    const existingScript = document.getElementById(
      GOOGLE_PLACES_SCRIPT_ID
    ) as HTMLScriptElement | null;
    if (existingScript) {
      if (existingScript.dataset.loaded === "true") {
        finishAfterLibrariesLoad(existingScript);
        return;
      }

      const timeoutId = window.setTimeout(async () => {
        const isLoaded = await ensureGoogleMapsLibraries();
        finish(isLoaded, isLoaded ? null : existingScript);
      }, GOOGLE_PLACES_SCRIPT_TIMEOUT_MS);

      existingScript.addEventListener("load", () => {
        window.clearTimeout(timeoutId);
        finishAfterLibrariesLoad(existingScript);
      });
      existingScript.addEventListener("error", () => {
        window.clearTimeout(timeoutId);
        finish(false, existingScript);
      });
      return;
    }

    const script = document.createElement("script");
    let timeoutId: number | undefined;
    script.id = GOOGLE_PLACES_SCRIPT_ID;
    (window as any)[GOOGLE_MAPS_CALLBACK_NAME] = () => {
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
      script.dataset.loaded = "true";
      finishAfterLibrariesLoad(script);
    };
    script.src = buildGoogleMapsScriptUrl(apiKey, languageCode);
    timeoutId = window.setTimeout(async () => {
      const isLoaded = await ensureGoogleMapsLibraries();
      finish(isLoaded, isLoaded ? null : script);
    }, GOOGLE_PLACES_SCRIPT_TIMEOUT_MS);

    script.async = true;
    script.onload = () => {
      script.dataset.loaded = "true";
    };
    script.onerror = () => {
      window.clearTimeout(timeoutId);
      finish(false, script);
    };
    document.head.appendChild(script);
  });

  return googlePlacesScriptPromise;
}

export async function getGooglePlacesLibrary() {
  const isLoaded = await loadGooglePlacesScript();
  const mapsApi = getGoogleMapsApi();

  if (!isLoaded || !mapsApi) {
    return undefined;
  }

  if (mapsApi.places?.AutocompleteSuggestion && mapsApi.places.Place) {
    return mapsApi.places;
  }

  try {
    const placesLibrary = await mapsApi.importLibrary?.("places");

    return placesLibrary?.AutocompleteSuggestion || placesLibrary?.Place
      ? placesLibrary
      : mapsApi.places?.AutocompleteSuggestion || mapsApi.places?.Place
      ? mapsApi.places
      : undefined;
  } catch (_) {
    return mapsApi.places?.AutocompleteSuggestion || mapsApi.places?.Place
      ? mapsApi.places
      : undefined;
  }
}

function mapGooglePlaceDetails(
  place: GooglePlaceDetails,
  placeId?: string,
  rawInput?: string
): EventLocationDetails {
  const latitude = place.geometry?.location?.lat();
  const longitude = place.geometry?.location?.lng();
  const address = place.formatted_address;

  return {
    address,
    city: getCityFromPlace(place),
    label: place.name || address,
    latitude,
    longitude,
    mapsUrl: place.url || buildGoogleMapsUrl(placeId, latitude, longitude),
    placeId,
    rawInput,
    source: "google_places",
  };
}

function mapGoogleModernPlaceDetails(
  place: GoogleModernPlace,
  rawInput?: string
): EventLocationDetails {
  const latitude = place.location?.lat();
  const longitude = place.location?.lng();
  const address = place.formattedAddress;
  const label = stringifyGoogleText(place.displayName) || address;
  const mapsUrl = stringifyGoogleText(place.googleMapsURI);

  return {
    address,
    city: getCityFromModernPlace(place),
    label,
    latitude,
    longitude,
    mapsUrl: mapsUrl || buildGoogleMapsUrl(place.id, latitude, longitude),
    placeId: place.id,
    rawInput,
    source: "google_places",
  };
}

export async function getGooglePlaceDetails(
  place?: GoogleModernPlace,
  rawInput?: string
): Promise<EventLocationDetails | undefined> {
  if (!place) {
    return undefined;
  }

  try {
    await place.fetchFields({
      fields: [
        "addressComponents",
        "displayName",
        "formattedAddress",
        "googleMapsURI",
        "id",
        "location",
      ],
    });
  } catch (_) {
    return undefined;
  }

  return mapGoogleModernPlaceDetails(place, rawInput);
}

export async function getGooglePlaceDetailsByPlaceId(
  placeId: string,
  rawInput?: string
): Promise<EventLocationDetails | undefined> {
  const placesApi = await getGooglePlacesLibrary();
  const Place = placesApi?.Place;

  if (!Place) {
    return undefined;
  }

  const { language, region } = getGoogleMapsLocale();

  return getGooglePlaceDetails(
    new Place({
      id: placeId,
      requestedLanguage: language,
      requestedRegion: region,
    }),
    rawInput
  );
}

async function getGoogleNearbyPlaceDetailsByCoordinates(
  coordinates: { latitude: number; longitude: number },
  rawInput: string
): Promise<EventLocationDetails | undefined> {
  const placesApi = await getGooglePlacesLibrary();
  const { language, region } = getGoogleMapsLocale();
  const response = await placesApi?.Place?.searchNearby?.({
    fields: [
      "addressComponents",
      "displayName",
      "formattedAddress",
      "googleMapsURI",
      "id",
      "location",
    ],
    language,
    locationRestriction: {
      center: {
        lat: coordinates.latitude,
        lng: coordinates.longitude,
      },
      radius: NEARBY_PLACE_SEARCH_RADIUS_METERS,
    },
    maxResultCount: 1,
    region,
  }).catch(() => undefined);
  const nearbyPlace = response?.places?.[0];

  return nearbyPlace ? getGooglePlaceDetails(nearbyPlace, rawInput) : undefined;
}

async function getGoogleLocationDetailsFromGeocoder(
  request:
    | {
        location: {
          lat: number;
          lng: number;
        };
      }
    | {
        address: string;
      },
  rawInput: string,
  fallbackCoordinates?: { latitude: number; longitude: number }
): Promise<EventLocationDetails | undefined> {
  const isLoaded = await loadGooglePlacesScript();
  const mapsApi = getGoogleMapsApi();

  if (!isLoaded || !mapsApi || !(await ensureGoogleGeocodingLibrary())) {
    return undefined;
  }

  return new Promise((resolve) => {
    const geocoder = new mapsApi.Geocoder();

    geocoder.geocode(
      { ...request, ...getGoogleMapsLocale() },
      (results, status) => {
        if (status !== mapsApi.GeocoderStatus.OK || !results?.length) {
          resolve(undefined);
          return;
        }

        const result = results[0];
        const fallbackGeometry = fallbackCoordinates
          ? {
              location: {
                lat: () => fallbackCoordinates.latitude,
                lng: () => fallbackCoordinates.longitude,
              },
            }
          : undefined;

        resolve(
          mapGooglePlaceDetails(
            {
              ...result,
              geometry: result.geometry || fallbackGeometry,
            },
            result.place_id,
            rawInput
          )
        );
      }
    );
  });
}

export async function getGoogleLocationDetailsByCoordinates(
  value: string
): Promise<EventLocationDetails | undefined> {
  const coordinates = parseLocationCoordinates(value);

  if (!coordinates) {
    return undefined;
  }

  return (
    (await getGoogleLocationDetailsFromGeocoder(
      {
        location: {
          lat: coordinates.latitude,
          lng: coordinates.longitude,
        },
      },
      value,
      coordinates
    )) || getGoogleNearbyPlaceDetailsByCoordinates(coordinates, value)
  );
}

export async function getGoogleLocationDetailsByAddress(
  value: string
): Promise<EventLocationDetails | undefined> {
  const address = value.trim();

  if (!address) {
    return undefined;
  }

  return getGoogleLocationDetailsFromGeocoder({ address }, value);
}
